import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, Eye, Play, Loader2, ArrowLeft, Youtube, Instagram, Facebook, Twitter, Music2, Globe, Video } from "lucide-react";
import { endpoints } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FacebookPageEmbed, TwitterTimelineEmbed, LinkOnlyPanel, OpenExternal, handleFromUrl } from "@/components/SocialEmbeds";

// Platform catalog. `mode` controls how content is surfaced inside BBKigali:
//  - "youtube": in-app video grid (plays on the in-app player, counts verified views)
//  - "embed":   live feed embedded inside BBKigali (member stays in the app)
//  - "link":    platform blocks full-feed embedding -> official link only
const CATALOG = [
  { key: "youtube", label: "YouTube", Icon: Youtube, color: "#FF0000", mode: "youtube" },
  { key: "facebook", label: "Facebook", Icon: Facebook, color: "#1877F2", mode: "embed" },
  { key: "twitter", label: "X / Twitter", Icon: Twitter, color: "#1DA1F2", mode: "embed" },
  { key: "instagram", label: "Instagram", Icon: Instagram, color: "#E1306C", mode: "link",
    note: "Instagram does not allow full profiles to be embedded in other apps. Tap below to view Alyn's latest photos and reels on Instagram." },
  { key: "tiktok", label: "TikTok", Icon: Music2, color: "#22D3EE", mode: "link",
    note: "TikTok does not allow full profiles to be embedded in other apps. Tap below to watch the latest videos on TikTok." },
  { key: "music", label: "Music", Icon: Music2, color: "#1DB954", mode: "link",
    note: "Stream the latest releases on your favourite music service." },
  { key: "website", label: "Website", Icon: Globe, color: "#94A3B8", mode: "link",
    note: "Visit the official website." },
];

export default function CreatorProfile() {
  const { slug } = useParams();
  const { isAuthed } = useAuth();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [active, setActive] = useState(null);
  const firedRef = useRef(new Set());

  useEffect(() => {
    endpoints.creatorProfile(slug)
      .then((r) => setC(r.data))
      .catch(() => setErr("Creator not found."))
      .finally(() => setLoading(false));
  }, [slug]);

  // Record a BBKigali-originated channel view (subscribed members only; backend dedups to once/30 days).
  const track = (channel) => {
    if (!isAuthed || !channel) return;
    if (firedRef.current.has(channel)) return;
    firedRef.current.add(channel);
    endpoints.creatorChannelView(slug, channel).catch(() => {});
  };

  const { links, platforms } = useMemo(() => {
    const s = (c && c.socials) || {};
    const yt = c?.youtubeHandle
      ? `https://www.youtube.com/${c.youtubeHandle.startsWith("@") ? c.youtubeHandle : "@" + c.youtubeHandle}`
      : (c?.youtubeChannelId ? `https://www.youtube.com/channel/${c.youtubeChannelId}` : "");
    const l = {
      youtube: (c?.videos?.length || yt) ? (yt || "#") : "",
      facebook: s.facebook, twitter: s.twitter, instagram: s.instagram,
      tiktok: s.tiktok, music: s.music, website: s.website,
    };
    const p = CATALOG.filter((pl) => l[pl.key]);
    return { links: l, platforms: p };
  }, [c]);

  useEffect(() => {
    if (platforms.length && !active) setActive(platforms[0].key);
  }, [platforms, active]);

  // Opening an embedded channel (Facebook/X) inside BBKigali counts as a channel view.
  useEffect(() => {
    const cur = platforms.find((p) => p.key === active);
    if (cur && cur.mode === "embed") track(active);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[#E11D2A]" /></div>;
  if (err) return <div className="text-center py-32 text-slate-400" data-testid="creator-not-found">{err}</div>;

  const current = platforms.find((p) => p.key === active);

  const VideoGrid = () => (
    (!c.videos || c.videos.length === 0) ? (
      <p className="text-slate-500" data-testid="creator-no-videos">No videos yet.</p>
    ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="creator-videos-grid">
        {c.videos.map((v) => (
          <Link key={v.id} to={`/creator-video/${v.id}`} data-testid={`creator-video-${v.id}`}
            className="group rounded-xl overflow-hidden bg-[#121620] border border-[#273042] hover:border-[#E11D2A]/60 transition-colors">
            <div className="relative aspect-video bg-black">
              <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="w-12 h-12 rounded-full bg-[#E11D2A] flex items-center justify-center"><Play className="w-5 h-5 text-white ml-0.5" /></span>
              </div>
            </div>
            <div className="p-3">
              <h3 className="text-white text-sm font-semibold line-clamp-2">{v.title}</h3>
              <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Eye className="w-3 h-3" /> {(v.eligibleViews || 0).toLocaleString()}</p>
            </div>
          </Link>
        ))}
      </div>
    )
  );

  const renderContent = () => {
    if (!current) return null;
    if (current.mode === "youtube") return <VideoGrid />;
    if (current.mode === "embed" && current.key === "facebook")
      return <FacebookPageEmbed url={links.facebook} testid="facebook-embed" />;
    if (current.mode === "embed" && current.key === "twitter")
      return <TwitterTimelineEmbed handle={handleFromUrl(links.twitter)} profileUrl={links.twitter} testid="twitter-embed" onOpen={() => track("twitter")} />;
    // link-only platforms
    return <LinkOnlyPanel platform={current.label} url={links[current.key]} color={current.color} Icon={current.Icon} note={current.note} onOpen={() => track(current.key)} />;
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="creator-profile-page">
      <Link to="/creators" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-6"><ArrowLeft className="w-4 h-4" /> All creators</Link>
      <div className="flex items-center gap-5 mb-8">
        <div className="w-24 h-24 rounded-full overflow-hidden bg-[#121620] border border-[#273042] shrink-0 flex items-center justify-center">
          {c.photoUrl ? <img src={c.photoUrl} alt={c.name} className="w-full h-full object-cover" /> : <Star className="w-10 h-10 text-[#E11D2A]" />}
        </div>
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white">{c.name}</h1>
          <p className="text-[#E11D2A] text-sm font-semibold mt-1">{c.category}</p>
          <p className="text-slate-400 text-sm mt-1 flex items-center gap-1.5"><Eye className="w-4 h-4" /> {(c.totalEligibleViews || 0).toLocaleString()} verified views</p>
        </div>
      </div>
      {c.bio && <p className="text-slate-300 mb-8 max-w-2xl">{c.bio}</p>}

      {platforms.length === 0 ? (
        <>
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Video className="w-5 h-5 text-[#E11D2A]" /> Videos</h2>
          <VideoGrid />
        </>
      ) : (
        <div data-testid="creator-platform-hub">
          <h2 className="text-lg font-bold text-white mb-4">Choose a platform</h2>
          <div className="flex flex-wrap gap-2.5 mb-7" role="tablist">
            {platforms.map(({ key, label, Icon, color }) => {
              const on = key === active;
              return (
                <button key={key} onClick={() => setActive(key)} data-testid={`platform-tab-${key}`} role="tab" aria-selected={on}
                  className={`flex items-center gap-2 h-11 px-5 rounded-full border text-sm font-semibold transition-all ${on ? "bg-white text-[#0A0C10] border-white shadow-lg" : "bg-[#121620] text-white border-[#273042] hover:border-white/40"}`}>
                  <Icon className="w-4 h-4" style={{ color }} />
                  {label}
                </button>
              );
            })}
          </div>

          <div className="rounded-2xl bg-[#0E121A] border border-[#273042] p-4 sm:p-6 min-h-[300px]" data-testid="platform-content">
            <div className="flex items-center justify-between mb-5">
              <p className="text-white font-bold flex items-center gap-2">
                {current && <current.Icon className="w-5 h-5" style={{ color: current.color }} />}
                {current?.label}
                {current?.mode === "embed" && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-500/15 text-green-400">LIVE IN APP</span>}
              </p>
              {current && links[current.key] && links[current.key] !== "#" && (
                <a href={links[current.key]} target="_blank" rel="noopener noreferrer" data-testid={`platform-official-${current.key}`}
                  onClick={() => track(current.key)}
                  className="text-xs text-slate-400 hover:text-white underline underline-offset-2">Official page</a>
              )}
            </div>
            {renderContent()}
          </div>
          {current?.mode === "youtube" && (
            <p className="text-[11px] text-slate-500 mt-3">Videos play right here inside B&B KIGALI — your watch time counts toward the creator's verified views.</p>
          )}
        </div>
      )}
    </div>
  );
}
